import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { UserRoundCog } from 'lucide-react';
import { getActiveUserId, listSessions, switchSession, type StoredSession } from '@/utils/secureToken';

/**
 * Stops a tab from silently acting as an account it was not opened as.
 *
 * Every tab shares one localStorage, so signing into (or switching to) another
 * account in tab B changes the token tab A sends on its very next request —
 * with nothing on screen in tab A saying so. On 2026-09-17 a Studio canvas
 * open on an admin-owned document kept running after a different account
 * signed in four seconds later in another tab: every autosave PATCH and the
 * document GET came back 404, and erase came back 400 ("Could not read the
 * image or the mask"), because the server — correctly — would not let the new
 * account touch the old account's document or image. The page looked broken;
 * the real problem was that it was no longer the same user.
 *
 * The `storage` event only fires in OTHER tabs, which is exactly the case to
 * catch: a switch made in this tab already navigates.
 */

function currentUserId(): string | null {
  try {
    const active = getActiveUserId();
    if (active) return active;
    const tok = localStorage.getItem('authToken');
    if (!tok) return null;
    return JSON.parse(atob(tok.split('.')[1] || ''))?.sub ?? null;
  } catch {
    return null;
  }
}

function label(s: StoredSession | undefined): string {
  return s?.email || s?.username || s?.displayName || '';
}

const WATCHED_KEYS = new Set(['auth_active', 'authToken', 'auth_sessions', null]);

export default function AccountChangeGuard() {
  const { t } = useTranslation();
  const tabUserRef = useRef<string | null>(null);
  // Snapshot of this tab's own session, so it can still be named after a
  // sign-out elsewhere has removed it from storage.
  const tabSessionRef = useRef<StoredSession | undefined>(undefined);
  const [change, setChange] = useState<{ from: StoredSession | undefined; to: StoredSession | undefined; fromId: string; fromLabel: string; signedOut: boolean } | null>(null);

  useEffect(() => {
    tabUserRef.current = currentUserId();
    tabSessionRef.current = listSessions().find((s) => s.userId === tabUserRef.current);
    const onStorage = (e: StorageEvent) => {
      // key === null means localStorage.clear() in another tab.
      if (!WATCHED_KEYS.has(e.key)) return;
      const fromId = tabUserRef.current;
      const toId = currentUserId();
      // A tab opened signed-out has nothing to protect; it picks the new
      // account up on its next navigation.
      if (!fromId || fromId === toId) {
        setChange(null);
        return;
      }
      const sessions = listSessions();
      setChange({
        fromId,
        signedOut: !toId,
        from: sessions.find((s) => s.userId === fromId),
        fromLabel: label(tabSessionRef.current),
        to: toId ? sessions.find((s) => s.userId === toId) : undefined,
      });
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  if (!change) return null;

  const fromName = label(change.from) || change.fromLabel;
  const toName = label(change.to) || t('common.anotherAccount', 'another account');
  // Switching back is only possible while the old session is still stored —
  // not after a sign-out.
  const canSwitchBack = !!change.from;

  const switchBack = () => {
    if (switchSession(change.fromId)) {
      window.dispatchEvent(new CustomEvent('soltik-web-auth-change', {
        detail: { token: change.from!.token, email: change.from!.email || '' },
      }));
      setChange(null);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-10001 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4" role="alertdialog" aria-modal="true" aria-labelledby="account-change-title">
      <div className="w-full max-w-md bg-[#16161e] border border-white/8 rounded-xl p-5 shadow-2xl">
        <div className="flex items-center gap-2 mb-2">
          <UserRoundCog size={18} className="text-amber-300 shrink-0" />
          <h2 id="account-change-title" className="text-sm font-semibold text-white">
            {t('common.accountChangedTitle', 'Your account changed in another tab')}
          </h2>
        </div>
        <p className="text-xs text-white/60 leading-relaxed mb-4">
          {!change.signedOut
            ? t('common.accountChangedBody', 'This tab was opened as {from}, but this browser is now signed in as {to}. Until you choose, anything saved here would go to the wrong account, and files that belong to {from} will fail to load.', { from: fromName || t('common.anotherAccount', 'another account'), to: toName })
            : t('common.accountSignedOutBody', 'This tab was opened as {from}, but that account was signed out in another tab. Changes made here can no longer be saved.', { from: fromName || t('common.anotherAccount', 'another account') })}
        </p>
        <div className="flex items-center justify-end gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-3 py-1.5 rounded-lg text-xs text-white/70 hover:text-white hover:bg-white/6"
          >
            {!change.signedOut
              ? t('common.accountContinueAs', 'Reload as {name}', { name: toName })
              : t('common.reloadPage', 'Reload page')}
          </button>
          {canSwitchBack && (
            <button
              type="button"
              onClick={switchBack}
              className="px-3 py-1.5 rounded-lg bg-amber-500 text-black text-xs font-semibold hover:bg-amber-400"
            >
              {t('common.accountSwitchBack', 'Switch back to {name}', { name: fromName || t('common.previousAccount', 'previous account') })}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
